import React, { useState, useEffect, useRef } from 'react';
import { Header, Loader } from 'semantic-ui-react';
import { getDashboardChart } from '~/api/admin';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { CHART_COLORS } from './chartColors';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
);

interface ChartData {
  labels: string[];
  mean: number[];
  median: number[];
}

interface DaysMatchToFirstBidChartProps {
  interval?: string;
  onIntervalChange?: (interval: string) => void;
  states?: string[];
}

const DaysMatchToFirstBidChart: React.FC<DaysMatchToFirstBidChartProps> = ({
  interval: intervalProp = 'all',
  states = [],
}) => {
  const [data, setData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const interval = intervalProp;
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<ChartJS | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params: Record<string, string> = { interval };
        if (states.length) params.states = states.join(',');
        const result = await getDashboardChart('days-match-to-first-bid', params);
        setData(result || null);
      } catch (error) {
        console.error('Error fetching days match to first bid chart data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [interval, states]);

  useEffect(() => {
    if (!loading && data?.labels?.length && chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      if (ctx) {
        chartInstance.current = new ChartJS(ctx, {
          type: 'line',
          data: {
            labels: data.labels,
            datasets: [
              {
                label: 'Median (days)',
                data: data.median,
                borderColor: CHART_COLORS.softBlue,
                backgroundColor: 'rgba(91, 155, 213, 0.1)',
                fill: false,
                tension: 0.3,
                pointRadius: 4,
                pointHoverRadius: 6,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
              },
              title: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: (context) =>
                    `${context.dataset.label}: ${context.parsed.y.toFixed(1)} days`,
                },
              },
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Month project was matched',
                },
              },
              y: {
                title: {
                  display: true,
                  text: 'Days',
                },
                beginAtZero: true,
                ticks: {
                  stepSize: 1,
                },
              },
            },
          },
        });
      }

      return () => {
        if (chartInstance.current) {
          chartInstance.current.destroy();
        }
      };
    }
  }, [data, loading]);

  if (loading) {
    return (
      <div className="card">
        <Header as="h2">Days from Match to First Bid</Header>
        <Loader active inline="centered" />
      </div>
    );
  }

  return (
    <div className="card">
      <Header as="h2">Days from Match to First Bid</Header>
      {data?.labels?.length ? (
        <div style={{ height: '400px', width: '100%' }}>
          <canvas ref={chartRef} />
        </div>
      ) : (
        <p>No data available</p>
      )}
    </div>
  );
};

export default DaysMatchToFirstBidChart;
